import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase/supabaseClient";

export default function ResetPasswordForm() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [passwordCheck, setPasswordCheck] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 이미 성공한 뒤엔 자동 이동(2초 후)
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(async () => {
      // 비번 재설정은 recovery 세션에서 이루어지므로, 깔끔하게 로그아웃 후 로그인 페이지로 보내는 게 자연스럽다
      await supabase.auth.signOut();
      navigate("/login", { replace: true }); // 없으면 "/" 등으로 바꿔도 OK
    }, 2000);
    return () => clearTimeout(t);
  }, [done, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || password.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    if (password !== passwordCheck) {
      setError("비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    setSubmitting(true);
    try {
      // supabase-js v2: 현재 세션(=recovery)에서 비밀번호 갱신
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw updateErr;

      setDone(true);
    } catch (err: any) {
      setError(err?.message ?? "비밀번호 변경 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  // 완료 화면
  if (done) {
    return (
      <div className="max-w-xl mx-auto p-6 bg-white rounded-2xl shadow">
        <h2 className="text-2xl font-bold mb-4">비밀번호 재설정 완료</h2>
        <p className="text-green-600 mb-3">
          비밀번호가 성공적으로 변경되었습니다.
        </p>
        <p className="text-gray-600">
          잠시 후 로그인 페이지로 이동합니다…
        </p>

        <div className="mt-6">
          <button
            className="w-full rounded-xl py-3 font-semibold bg-black text-white"
            onClick={async () => {
              await supabase.auth.signOut();
              navigate("/login", { replace: true });
            }}
          >
            지금 바로 로그인 하러 가기
          </button>
        </div>
      </div>
    );
  }

  // 폼 화면
  return (
    <form onSubmit={onSubmit} className="max-w-xl mx-auto p-6 bg-white rounded-2xl shadow">
      <h2 className="text-2xl font-bold mb-6">비밀번호 재설정</h2>

      <label className="block text-sm mb-2">새 비밀번호</label>
      <input
        type="password"
        className="w-full border rounded-lg px-3 py-3 mb-4"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="새 비밀번호를 입력하세요"
        autoComplete="new-password"
      />

      <label className="block text-sm mb-2">새 비밀번호 확인</label>
      <input
        type="password"
        className="w-full border rounded-lg px-3 py-3 mb-2"
        value={passwordCheck}
        onChange={(e) => setPasswordCheck(e.target.value)}
        placeholder="다시 한 번 입력하세요"
        autoComplete="new-password"
      />

      {error && <p className="text-red-600 mt-2 mb-4">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-xl py-3 font-semibold bg-black text-white disabled:opacity-60"
      >
        {submitting ? "변경 중…" : "비밀번호 변경"}
      </button>
    </form>
  );
}